import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { PeriodPicker } from "@/components/PeriodPicker";
import { StableInput, StableTextarea } from "@/components/StableField";
import { usePersistedBoolean } from "@/lib/usePersistedBoolean";
import { StatusBadge, InstallmentBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createPayment,
  deletePayment,
  updatePayment,
  listCategories,
  listExpenses,
  listOccurrences,
  listPayments,
  getOccurrence,
  listVendors,
  patchOccurrence,
  postponeOccurrence,
  skipOccurrence,
  markOccurrenceNotDue,
  restoreOccurrence,
} from "@/lib/api";
import { formatApiError } from "@/lib/api-error";
import { newIdempotencyKey } from "@/lib/idempotency";
import { EUR, fmtDate, monthBounds, paymentMethodIT, paymentMethodOptions, recurrenceIT, statusIT } from "@/lib/format";
import { ExportButtons } from "@/components/ExportButtons";
import { buildFilename } from "@/lib/export";
import { OccurrencesCalendar } from "@/components/OccurrencesCalendar";
import { NuovaScadenzaChoiceDialog } from "@/components/NuovaScadenzaChoiceDialog";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { CalendarRange, ChevronDown, ChevronRight, List as ListIcon, MoreHorizontal, SlidersHorizontal } from "lucide-react";
import {
  TagAssignmentPopover,
  TagFilter,
  TagList,
  useEntityTags,
} from "@/components/TagComponents";
import {
  DocumentBadge,
  DocumentReferenceFields,
  useDocumentTypes,
} from "@/components/DocumentComponents";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { SortableHeader, makeSorter } from "@/components/SortableHeader";

const STATUSES = ["planned", "partially_paid", "paid", "skipped", "cancelled", "not_due"];
const CLOSED_STATUSES = ["paid", "cancelled", "skipped", "not_due"];
const RECURRENCES = ["one_time", "monthly", "bi_monthly", "quarterly", "every_4_months", "semi_annual", "annual"];

export default function Occurrences() {
  const [params, setParams] = useSearchParams();
  const [rawRows, setRawRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [expenses, setExpenses] = useState({});
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState("");
  const [editNotes, setEditNotes] = useState("");
  // v1.59.1 ck14.2.b: campi Riferimento Documento nel modal edit occurrence
  const [editDoc, setEditDoc] = useState({});

  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payRef, setPayRef] = useState("");
  const [payMethod, setPayMethod] = useState("bank_transfer");
  // v1.59.1 ck14.2.b: campi Riferimento Documento per il pagamento
  // (default: eredita dai doc_* dell'occurrence, l'utente può modificare).
  const [payDoc, setPayDoc] = useState({});
  // v1.60.3: Idempotency-Key per Registra pagamento (nuova ad ogni
  // apertura, invariata durante i retry, consumata al successo).
  const [payIdemKey, setPayIdemKey] = useState(() => newIdempotencyKey());

  const [postOpen, setPostOpen] = useState(false);
  const [postRow, setPostRow] = useState(null);
  const [postDate, setPostDate] = useState("");

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRow, setHistoryRow] = useState(null);
  const [historyPayments, setHistoryPayments] = useState([]);

  const [newChoiceOpen, setNewChoiceOpen] = useState(false);
  const [notDueTarget, setNotDueTarget] = useState(null);

  const filters = useMemo(
    () => ({
      date_from: params.get("date_from") || `${new Date().getFullYear()}-01-01`,
      date_to: params.get("date_to") || `${new Date().getFullYear()}-12-31`,
      status: params.get("status") || "",
      category_id: params.get("category_id") || "",
      vendor_id: params.get("vendor_id") || "",
      recurrence_type: params.get("recurrence_type") || "",
      method: params.get("method") || "",
      // Ordinary expenses now behave as "prima nota già pagata": they are
      // hidden from Scadenze spese by default. The user can opt-in.
      include_ordinary: params.get("include_ordinary") === "1",
    }),
    [params],
  );

  const setIncludeOrdinary = (next) => {
    const p = new URLSearchParams(params);
    if (next) p.set("include_ordinary", "1");
    else p.delete("include_ordinary");
    setParams(p, { replace: true });
  };

  // Hide "paid" by default unless explicitly set, OR the user is filtering by status.
  const showPaid = params.get("show_paid") === "1";
  const setShowPaid = (next) => {
    const p = new URLSearchParams(params);
    if (next) p.set("show_paid", "1");
    else p.delete("show_paid");
    setParams(p, { replace: true });
  };

  const view = params.get("view") === "calendar" ? "calendar" : "list";
  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "calendar") next.set("view", "calendar");
    else next.delete("view");
    setParams(next, { replace: true });
  };

  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(() => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  }, []);

  // Snellimento prestazioni (iter38): aggiorna una singola riga in place,
  // così le azioni puntuali (Paga, Modifica, Skip, NotDue, Restore, Postpone)
  // NON triggerano un refetch globale di tutte le occurrences (che su DB con
  // 160+ righe è il principale responsabile del lag percepito durante il
  // field testing).
  const mergeRow = useCallback((updated) => {
    if (!updated || !updated.id) return;
    setRawRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      const query = Object.fromEntries(
        Object.entries(filters)
          .filter(([k, v]) => v && v !== "all" && k !== "method"),
      );
      query.limit = 500;
      // Scadenze Spese mostra sempre TUTTE le scadenze operative
      // (ricorrenti aperte + ordinarie aperte). Le ordinarie GIÀ PAGATE
      // sono nascoste di default perché vivono in /ordinary come voci
      // di prima nota; l'utente può comunque vederle attivando il toggle
      // "+ ordinarie" che le reinclude nel listato.
      // NB: lasciamo il fetch backend includere le ordinarie e filtriamo
      // client-side solo le ordinarie chiuse — così le ordinarie APERTE
      // (workflow "Da pagare") restano sempre visibili anche con toggle OFF.
      const includeOrdinary = !!filters.include_ordinary;
      // The toggle itself is not a backend filter — strip it.
      delete query.include_ordinary;
      // Only the occurrences list depends on filters; the static lookups
      // (categories, vendors, expenses) are loaded once via the dedicated
      // useEffect below.
      const occ = await listOccurrences(query);
      let filtered = occ || [];
      if (!includeOrdinary) {
        // Nascondi solo le ordinarie CHIUSE; le ordinarie aperte (planned,
        // partially_paid, overdue) restano sempre visibili in Scadenze Spese.
        const closedStatuses = new Set([
          "paid", "cancelled", "skipped", "not_due",
        ]);
        filtered = filtered.filter(
          (r) => !r.is_ordinary || !closedStatuses.has(r.status),
        );
      }
      // Method filter: keep occurrences that have at least one payment with the
      // selected method in the active date window. Payment-side filter is not
      // supported by the occurrences endpoint, so we resolve it on the client.
      if (filters.method) {
        const pays = await listPayments({
          method: filters.method,
          date_from: filters.date_from,
          date_to: filters.date_to,
          limit: 5000,
        });
        const matchingOccIds = new Set((pays || []).map((p) => p.occurrence_id));
        filtered = filtered.filter((r) => matchingOccIds.has(r.id));
      }
      if (cancelled) return;
      setRawRows(filtered);
      setLoading(false);
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [filters, reloadKey]);

  // Static lookups — fetched ONCE on mount, never again. Filter changes do
  // not touch these (previously they were re-fetched on every filter change,
  // causing the perceived "pesantezza" when navigating between months).
  useEffect(() => {
    (async () => {
      try {
        const [cats, vens, exps] = await Promise.all([
          listCategories(),
          listVendors(),
          listExpenses(),
        ]);
        setCategories(cats || []);
        setVendors(vens || []);
        setExpenses(Object.fromEntries((exps || []).map((e) => [e.id, e])));
      } catch {
        /* non-fatal: the lookups are only used to enrich the table */
      }
    })();
  }, []);

  const rows = useMemo(() => {
    if (showPaid || CLOSED_STATUSES.includes(filters.status)) return rawRows;
    return rawRows.filter((r) => !CLOSED_STATUSES.includes(r.status));
  }, [rawRows, showPaid, filters.status]);

  const paidHiddenCount = useMemo(
    () =>
      showPaid || CLOSED_STATUSES.includes(filters.status)
        ? 0
        : rawRows.filter((r) => CLOSED_STATUSES.includes(r.status)).length,
    [rawRows, showPaid, filters.status],
  );

  // Quick date filters
  const applyDateRange = (from, to) => {
    const next = new URLSearchParams(params);
    next.set("date_from", from);
    next.set("date_to", to);
    setParams(next, { replace: true });
  };
  const applyMonthOffset = (offset) => {
    const now = new Date();
    const target = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const [from, to] = monthBounds(target);
    applyDateRange(from, to);
  };
  const applyYear = (y) => {
    applyDateRange(`${y}-01-01`, `${y}-12-31`);
  };
  const applyMonthValue = (val) => {
    if (!val) return;
    const [y, m] = val.split("-").map((s) => parseInt(s, 10));
    const [from, to] = monthBounds(new Date(y, m - 1, 1));
    applyDateRange(from, to);
  };
  const monthOptions = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear() - 1, today.getMonth(), 1);
    const months = [];
    for (let i = 0; i < 36; i += 1) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d
        .toLocaleDateString("it-IT", { month: "long", year: "numeric" })
        .replace(/^./, (c) => c.toUpperCase());
      months.push({ value, label });
    }
    return months;
  }, []);
  const selectedMonthValue = useMemo(() => {
    // If date_from is the 1st of a month and date_to is the last day of the same month, return it.
    // Return empty string instead of undefined so the controlled Select doesn't
    // flip uncontrolled→controlled when the user picks a month (React warning).
    if (!filters.date_from || !filters.date_to) return "";
    const [yF, mF, dF] = filters.date_from.split("-");
    const [yT, mT] = filters.date_to.split("-");
    if (yF !== yT || mF !== mT || dF !== "01") return "";
    const lastDay = new Date(
      parseInt(yF, 10),
      parseInt(mF, 10),
      0,
    ).getDate();
    if (filters.date_to !== `${yF}-${mF}-${String(lastDay).padStart(2, "0")}`) {
      return "";
    }
    return `${yF}-${mF}`;
  }, [filters.date_from, filters.date_to]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (!value || value === "all") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const openEdit = (r) => {
    setEditRow(r);
    setEditAmount(r.planned_amount ?? r.effective_amount ?? "");
    setEditNotes(r.notes ?? "");
    setEditDoc({
      doc_type_id: r.doc_type_id || null,
      doc_number: r.doc_number || "",
      doc_date: r.doc_date || "",
      doc_protocol: r.doc_protocol || "",
      doc_notes: r.doc_notes || "",
    });
    setEditOpen(true);
  };
  const submitEdit = async () => {
    try {
      const updated = await patchOccurrence(editRow.id, {
        planned_amount: editAmount ? String(editAmount) : null,
        notes: editNotes || null,
        doc_type_id: editDoc.doc_type_id || null,
        doc_number: editDoc.doc_number || null,
        doc_date: editDoc.doc_date || null,
        doc_protocol: editDoc.doc_protocol || null,
        doc_notes: editDoc.doc_notes || null,
      });
      toast.success("Scadenza aggiornata (solo questa)");
      setEditOpen(false);
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    }
  };

  const openPay = (r) => {
    setPayRow(r);
    const remaining =
      Number(r.effective_amount || 0) - Number(r.total_paid || 0);
    setPayAmount(remaining > 0 ? remaining.toFixed(2) : r.effective_amount);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayRef("");
    setPayMethod("bank_transfer");
    // Il pagamento eredita i doc_* dell'occurrence come default (utente
    // può sovrascrivere). Se occurrence non ha ref, campi vuoti.
    setPayDoc({
      doc_type_id: r.doc_type_id || null,
      doc_number: r.doc_number || "",
      doc_date: r.doc_date || "",
      doc_protocol: r.doc_protocol || "",
      doc_notes: "",
    });
    // v1.60.3: nuova operazione = nuova chiave idempotenza.
    setPayIdemKey(newIdempotencyKey());
    setPayOpen(true);
  };
  const submitPay = async () => {
    try {
      await createPayment({
        occurrence_id: payRow.id,
        paid_amount: String(payAmount),
        paid_at: payDate,
        reference: payRef || null,
        method: payMethod,
        doc_type_id: payDoc.doc_type_id || null,
        doc_number: payDoc.doc_number || null,
        doc_date: payDoc.doc_date || null,
        doc_protocol: payDoc.doc_protocol || null,
        doc_notes: payDoc.doc_notes || null,
      }, { idempotencyKey: payIdemKey });
      toast.success("Pagamento registrato");
      setPayOpen(false);
      // v1.60.3: consuma la chiave usata.
      setPayIdemKey(newIdempotencyKey());
      // Refresh puntuale della singola riga interessata (no reload globale).
      try {
        const updated = await getOccurrence(payRow.id);
        mergeRow(updated);
      } catch {
        /* best-effort: i totali in pagina si aggiorneranno al prossimo cambio filtri */
      }
    } catch (e) {
      if (e?.isIdempotencyConflict) {
        toast.error(e.message);
      } else {
        toast.error(formatApiError(e, "Errore registrazione pagamento"));
      }
    }
  };

  const openPostpone = (r) => {
    setPostRow(r);
    setPostDate(r.due_date);
    setPostOpen(true);
  };
  const submitPostpone = async () => {
    try {
      const updated = await postponeOccurrence(postRow.id, { new_due_date: postDate });
      toast.success("Scadenza rinviata");
      setPostOpen(false);
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore rinvio"));
    }
  };

  const doSkip = async (r) => {
    if (!window.confirm("Saltare questa scadenza?")) return;
    try {
      const updated = await skipOccurrence(r.id);
      toast.success("Scadenza saltata");
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const doMarkNotDue = (r) => {
    setNotDueTarget(r);
  };

  const confirmMarkNotDue = async () => {
    if (!notDueTarget) return;
    try {
      const updated = await markOccurrenceNotDue(notDueTarget.id);
      toast.success("Scadenza segnata come non dovuta.");
      setNotDueTarget(null);
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const doRestore = async (r) => {
    try {
      const updated = await restoreOccurrence(r.id);
      toast.success("Scadenza ripristinata come pianificata.");
      mergeRow(updated);
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const openHistory = async (r) => {
    setHistoryRow(r);
    setHistoryOpen(true);
    const pays = await listPayments({ occurrence_id: r.id });
    setHistoryPayments(pays || []);
  };

  // Confirm dialog state for payment deletion (replaces window.confirm).
  const [deletePayTarget, setDeletePayTarget] = useState(null);

  const deletePaymentAndReload = (pid) => {
    setDeletePayTarget(pid);
  };

  const confirmDeletePayment = async () => {
    const pid = deletePayTarget;
    if (!pid) return;
    try {
      await deletePayment(pid);
      // Aggiorna solo lo storico locale e la singola riga: evita reload
      // globale (richiesto per le prestazioni in field testing).
      setHistoryPayments((prev) => (prev || []).filter((x) => x.id !== pid));
      if (historyRow) {
        try {
          const updated = await getOccurrence(historyRow.id);
          mergeRow(updated);
        } catch {
          /* update best-effort, niente reload globale */
        }
      }
      toast.success("Pagamento eliminato");
    } catch (e) {
      toast.error(formatApiError(e, "Errore eliminazione pagamento"));
    } finally {
      setDeletePayTarget(null);
    }
  };

  // Edit-payment dialog state — used to fix wrong dates / amount / method.
  const [editPayment, setEditPayment] = useState(null);
  const [epAmount, setEpAmount] = useState("");
  const [epDate, setEpDate] = useState("");
  const [epMethod, setEpMethod] = useState("bank_transfer");
  const [epRef, setEpRef] = useState("");
  // v1.59.1 ck14.2.b: campi doc_* nel modal "Modifica pagamento".
  const [epDoc, setEpDoc] = useState({});

  const openEditPayment = (p) => {
    setEditPayment(p);
    setEpAmount(String(p.paid_amount));
    setEpDate(p.paid_at);
    setEpMethod(p.method || "bank_transfer");
    setEpRef(p.reference || "");
    setEpDoc({
      doc_type_id: p.doc_type_id || null,
      doc_number: p.doc_number || "",
      doc_date: p.doc_date || "",
      doc_protocol: p.doc_protocol || "",
      doc_notes: p.doc_notes || "",
    });
  };
  const submitEditPayment = async () => {
    try {
      await updatePayment(editPayment.id, {
        paid_amount: String(epAmount),
        paid_at: epDate,
        method: epMethod,
        reference: epRef || null,
        doc_type_id: epDoc.doc_type_id || null,
        doc_number: epDoc.doc_number || null,
        doc_date: epDoc.doc_date || null,
        doc_protocol: epDoc.doc_protocol || null,
        doc_notes: epDoc.doc_notes || null,
      });
      toast.success("Pagamento aggiornato");
      setEditPayment(null);
      const pays = await listPayments({ occurrence_id: historyRow.id });
      setHistoryPayments(pays || []);
      // Aggiornamento puntuale dell'occurrence interessata invece di reload globale.
      try {
        const updated = await getOccurrence(historyRow.id);
        mergeRow(updated);
      } catch {
        /* best-effort */
      }
    } catch (e) {
      toast.error(formatApiError(e, "Errore aggiornamento pagamento"));
    }
  };

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const venMap = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v])), [vendors]);

  // Sorting state — local to the page (does not affect filters / URL)
  const [sort, setSort] = useState({ field: "due_date", dir: "asc" });

  // Etichette: filter state + batch fetch dei tag su tutte le rows.
  const [tagFilter, setTagFilter] = useState({ selectedIds: [], mode: "or", untaggedOnly: false });
  const occurrenceIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const { tagsByEntity, refetch: refetchTags } = useEntityTags(
    "occurrence",
    occurrenceIds,
  );

  // v1.59.1 ck14.2.b: mappa tipi documento per rendering badge
  const documentTypes = useDocumentTypes(true);
  const documentTypesById = useMemo(() => {
    const m = {};
    for (const t of documentTypes) m[t.id] = t;
    return m;
  }, [documentTypes]);

  const sortedRows = useMemo(() => {
    // Applica filtro tag prima dell'ordinamento
    let base = rows;
    if (tagFilter.untaggedOnly) {
      base = rows.filter((r) => (tagsByEntity[r.id] || []).length === 0);
    } else if (tagFilter.selectedIds.length > 0) {
      base = rows.filter((r) => {
        const rowTagIds = new Set(
          (tagsByEntity[r.id] || []).map((t) => t.id),
        );
        if (tagFilter.mode === "and") {
          return tagFilter.selectedIds.every((id) => rowTagIds.has(id));
        }
        return tagFilter.selectedIds.some((id) => rowTagIds.has(id));
      });
    }
    const list = [...base];
    const sorter = makeSorter(sort, (r, field) => {
      const exp = expenses[r.expense_id];
      const catId = r.category_id_override || exp?.category_id;
      const venId = r.vendor_id_override || exp?.vendor_id;
      switch (field) {
        case "due_date":
          return r.due_date;
        case "title":
          return (exp?.title || "").toLowerCase();
        case "vendor":
          return venMap[venId]?.name || "";
        case "category":
          return catMap[catId]?.name || "";
        case "recurrence_type":
          return recurrenceIT[exp?.recurrence_type] || "";
        case "status":
          return r.status;
        case "planned":
          return Number(r.effective_amount || 0);
        default:
          return null;
      }
    });
    if (sorter) list.sort(sorter);
    return list;
  }, [rows, sort, expenses, venMap, catMap, tagFilter, tagsByEntity]);

  /* ---------- export ---------- */
  const exportRowsData = useMemo(
    () =>
      rows.map((r) => {
        const exp = expenses[r.expense_id];
        const catId = r.category_id_override || exp?.category_id;
        const venId = r.vendor_id_override || exp?.vendor_id;
        const variance =
          Number(r.effective_amount || 0) - Number(r.total_paid || 0);
        return {
          due_date: r.due_date,
          original_due_date: r.original_due_date,
          postponed: r.original_due_date !== r.due_date ? "Sì" : "No",
          sequence_no: r.sequence_no,
          title: exp?.title || "",
          recurrence_type: recurrenceIT[exp?.recurrence_type] || "",
          status: statusIT[r.status] || r.status,
          modified: r.is_modified ? "Sì" : "No",
          vendor_name: venMap[venId]?.name || "",
          category_name: catMap[catId]?.name || "",
          planned: r.effective_amount,
          paid: r.total_paid,
          variance,
          remaining: variance,
          notes: r.notes || "",
        };
      }),
    [rows, expenses, venMap, catMap],
  );

  const exportColumns = useMemo(
    () => [
      { key: "due_date", header: "Scadenza", type: "date" },
      { key: "original_due_date", header: "Scadenza originale", type: "date" },
      { key: "postponed", header: "Rinviata" },
      { key: "sequence_no", header: "Seq.", type: "integer" },
      { key: "title", header: "Descrizione" },
      { key: "recurrence_type", header: "Ricorrenza" },
      { key: "status", header: "Stato" },
      { key: "modified", header: "Modificata" },
      { key: "vendor_name", header: "Fornitore" },
      { key: "category_name", header: "Categoria" },
      { key: "planned", header: "Pianificato (€)", type: "currency" },
      { key: "paid", header: "Pagato (€)", type: "currency" },
      { key: "variance", header: "Δ residuo (€)", type: "currency" },
      { key: "notes", header: "Note" },
    ],
    [],
  );

  const exportFilenameBase = useMemo(
    () => buildFilename("scadenze", filters),
    [filters],
  );

  // PDF-specific column subset (fewer columns than xlsx, optimized for print).
  const pdfColumns = useMemo(
    () => [
      { key: "due_date", header: "Scadenza", type: "date" },
      { key: "title", header: "Descrizione" },
      { key: "vendor_name", header: "Fornitore" },
      { key: "category_name", header: "Categoria" },
      { key: "status", header: "Stato" },
      { key: "planned", header: "Pianificato", type: "currency" },
      { key: "paid", header: "Pagato", type: "currency" },
      {
        key: "remaining",
        header: "Residuo",
        type: "currency",
      },
    ],
    [],
  );

  // Map exportRowsData onto a derived list with `remaining` populated for PDF.
  const pdfTotals = useMemo(() => {
    const planned = exportRowsData.reduce(
      (s, r) => s + Number(r.planned || 0),
      0,
    );
    const paid = exportRowsData.reduce(
      (s, r) => s + Number(r.paid || 0),
      0,
    );
    return {
      planned,
      paid,
      remaining: planned - paid,
      count: exportRowsData.length,
    };
  }, [exportRowsData]);

  const pdfSubtitle = useMemo(() => {
    const parts = [];
    if (filters.date_from && filters.date_to) {
      const fmt = (s) => {
        const [y, m, d] = s.split("-");
        return `${d}/${m}/${y}`;
      };
      parts.push(`Periodo: ${fmt(filters.date_from)} – ${fmt(filters.date_to)}`);
    }
    if (filters.status) parts.push(`Stato: ${filters.status}`);
    if (filters.recurrence_type) parts.push(`Ricorrenza: ${filters.recurrence_type}`);
    if (filters.vendor_id) {
      const v = vendors.find((x) => x.id === filters.vendor_id);
      if (v) parts.push(`Fornitore: ${v.name}`);
    }
    if (filters.category_id) {
      const c = categories.find((x) => x.id === filters.category_id);
      if (c) parts.push(`Categoria: ${c.name}`);
    }
    if (!showPaid && !CLOSED_STATUSES.includes(filters.status)) {
      parts.push("Chiuse nascoste");
    }
    return parts.join(" · ");
  }, [filters, showPaid, vendors, categories]);

  // UI/UX — collapsible panels with persisted state (densità operativa)
  const [filtersOpen, setFiltersOpen] = usePersistedBoolean(
    "occurrences:filtersOpen",
    true,
  );
  const [categoryStripOpen, setCategoryStripOpen] = usePersistedBoolean(
    "occurrences:categoryStripOpen",
    true,
  );

  return (
    <div data-testid="occurrences-page">
      <PageHeader
        testId="occurrences-header"
        title="Scadenze spese"
        subtitle="Istanze pianificate ed effettive"
        actions={
          <>
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => v && setView(v)}
              data-testid="view-toggle"
            >
              <ToggleGroupItem value="list" data-testid="view-list">
                <ListIcon size={14} strokeWidth={1.8} className="mr-1" />
                Lista
              </ToggleGroupItem>
              <ToggleGroupItem value="calendar" data-testid="view-calendar">
                <CalendarRange size={14} strokeWidth={1.8} className="mr-1" />
                Calendario
              </ToggleGroupItem>
            </ToggleGroup>
            <ExportButtons
              rows={exportRowsData}
              columns={exportColumns}
              filenameBase={exportFilenameBase}
              sheetName="Scadenze"
              testId="export-occurrences"
              pdf={{
                title: "Scadenze",
                subtitle: pdfSubtitle,
                rows: exportRowsData,
                columns: pdfColumns,
                totals: pdfTotals,
                filename: exportFilenameBase,
                orientation: "landscape",
              }}
            />
            <Button
              data-testid="btn-new-expense"
              onClick={() => setNewChoiceOpen(true)}
            >
              + Nuova scadenza
            </Button>
          </>
        }
      />

      <div
        data-testid="occurrences-quick-filters"
        className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-2"
      >
        <span className="text-[10px] uppercase tracking-wider text-stone-500">
          Periodo:
        </span>
        <PeriodPicker
          value={{ from: filters.date_from, to: filters.date_to }}
          onChange={(from, to) => applyDateRange(from, to)}
          testId="occ-period"
        />
        <Button
          size="sm"
          variant="outline"
          data-testid="qf-prev-month"
          onClick={() => applyMonthOffset(-1)}
          className="h-7 px-2 text-xs"
        >
          Mese precedente
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid="qf-current-month"
          onClick={() => applyMonthOffset(0)}
          className="h-7 px-2 text-xs"
        >
          Mese corrente
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid="qf-next-month"
          onClick={() => applyMonthOffset(1)}
          className="h-7 px-2 text-xs"
        >
          Prossimo mese
        </Button>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Label
              htmlFor="show-paid-toggle"
              className="text-[10px] uppercase tracking-wider text-stone-500"
              title="Mostra anche scadenze pagate, annullate, saltate o non dovute"
            >
              Mostra chiuse
            </Label>
            <Switch
              id="show-paid-toggle"
              data-testid="toggle-show-paid"
              checked={showPaid}
              onCheckedChange={setShowPaid}
            />
            {paidHiddenCount > 0 && (
              <span className="text-[10px] text-stone-400">
                ({paidHiddenCount})
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Label
              htmlFor="include-ordinary-toggle"
              className="text-[10px] uppercase tracking-wider text-stone-500"
              title="Mostra anche le spese ordinarie GIÀ PAGATE (di default sono solo in /ordinary). Le ordinarie APERTE sono sempre visibili qui."
            >
              + ord. chiuse
            </Label>
            <Switch
              id="include-ordinary-toggle"
              data-testid="toggle-include-ordinary"
              checked={filters.include_ordinary}
              onCheckedChange={setIncludeOrdinary}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="toggle-filters"
            onClick={() => setFiltersOpen((v) => !v)}
            className="h-7 px-2 text-xs"
            title={filtersOpen ? "Nascondi filtri avanzati" : "Mostra filtri avanzati"}
          >
            <SlidersHorizontal size={12} className="mr-1" />
            Filtri
            {filtersOpen ? (
              <ChevronDown size={12} className="ml-1" />
            ) : (
              <ChevronRight size={12} className="ml-1" />
            )}
          </Button>
          <TagFilter
            selectedIds={tagFilter.selectedIds}
            mode={tagFilter.mode}
            untaggedOnly={tagFilter.untaggedOnly}
            onChange={setTagFilter}
            testIdPrefix="occurrences-tag-filter"
          />
        </div>
      </div>

      {filtersOpen && (
      <div
        data-testid="occurrences-filters"
        className="mb-3 grid grid-cols-2 gap-3 rounded-xl border border-stone-200 bg-white p-3 md:grid-cols-7"
      >
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Dal
          </Label>
          <Input
            type="date"
            data-testid="filter-date-from"
            value={filters.date_from}
            onChange={(e) => setFilter("date_from", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Al
          </Label>
          <Input
            type="date"
            data-testid="filter-date-to"
            value={filters.date_to}
            onChange={(e) => setFilter("date_to", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Stato
          </Label>
          <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{statusIT[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Categoria
          </Label>
          <Select
            value={filters.category_id || "all"}
            onValueChange={(v) => setFilter("category_id", v)}
          >
            <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Fornitore
          </Label>
          <Select
            value={filters.vendor_id || "all"}
            onValueChange={(v) => setFilter("vendor_id", v)}
          >
            <SelectTrigger data-testid="filter-vendor"><SelectValue placeholder="Tutti" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {vendors.map((v) => (
                <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Ricorrenza
          </Label>
          <Select
            value={filters.recurrence_type || "all"}
            onValueChange={(v) => setFilter("recurrence_type", v)}
          >
            <SelectTrigger data-testid="filter-recurrence"><SelectValue placeholder="Tutte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {RECURRENCES.map((r) => (
                <SelectItem key={r} value={r}>{recurrenceIT[r]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-stone-500">
            Metodo
          </Label>
          <Select
            value={filters.method || "all"}
            onValueChange={(v) => setFilter("method", v)}
          >
            <SelectTrigger data-testid="filter-method">
              <SelectValue placeholder="Tutti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {paymentMethodOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      )}

      <PeriodExposurePill
        rawRows={rawRows}
        loading={loading}
        filters={filters}
        testId="occurrences-exposure-pill"
      />

      <CategoryKpiStrip
        rows={rows}
        expenses={expenses}
        catMap={catMap}
        filters={filters}
        onPick={(catId) => setFilter("category_id", catId)}
        activeCategoryId={filters.category_id}
        open={categoryStripOpen}
        onToggle={() => setCategoryStripOpen((v) => !v)}
      />

      <div
        className="overflow-hidden rounded-xl border border-stone-200 bg-white"
        hidden={view !== "list"}
      >
        {/* Sortable header bar */}
        <div
          data-testid="occurrences-list-header"
          className="hidden grid-cols-[100px_minmax(0,1fr)_140px_220px] items-center gap-4 border-b border-stone-200 bg-stone-50/60 px-4 py-2 lg:grid"
        >
          <SortableHeader field="due_date" sort={sort} onChange={setSort}>
            Scadenza
          </SortableHeader>
          <SortableHeader field="title" sort={sort} onChange={setSort}>
            Descrizione
          </SortableHeader>
          <div className="flex justify-end">
            <SortableHeader field="planned" sort={sort} onChange={setSort} align="right">
              Importo
            </SortableHeader>
          </div>
          <div className="flex justify-end">
            <SortableHeader field="status" sort={sort} onChange={setSort} align="right">
              Stato · Azioni
            </SortableHeader>
          </div>
        </div>

        {/* Mobile sort selector */}
        <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-5 py-3 lg:hidden">
          <span className="text-[11px] font-medium uppercase tracking-wider text-stone-500">
            Ordina per
          </span>
          <Select
            value={sort.field || ""}
            onValueChange={(v) => setSort({ field: v, dir: sort.dir || "asc" })}
          >
            <SelectTrigger className="h-9 w-[180px]" data-testid="sort-mobile-field">
              <SelectValue placeholder="Ordina" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="due_date">Scadenza</SelectItem>
              <SelectItem value="title">Descrizione</SelectItem>
              <SelectItem value="vendor">Fornitore</SelectItem>
              <SelectItem value="category">Categoria</SelectItem>
              <SelectItem value="recurrence_type">Ricorrenza</SelectItem>
              <SelectItem value="planned">Importo</SelectItem>
              <SelectItem value="status">Stato</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            data-testid="sort-mobile-dir"
            onClick={() =>
              setSort({ ...sort, dir: sort.dir === "asc" ? "desc" : "asc" })
            }
          >
            {sort.dir === "desc" ? "↓ Decrescente" : "↑ Crescente"}
          </Button>
        </div>

        {loading && (
          <div className="px-5 py-6 text-center text-sm text-stone-500">
            Caricamento…
          </div>
        )}
        {!loading && sortedRows.length === 0 && (
          <div className="px-5 py-6 text-center text-sm text-stone-500">
            Nessuna scadenza per i filtri correnti.
          </div>
        )}
        <ul className="divide-y divide-stone-100">
          {sortedRows.map((r) => {
            const postponed = r.original_due_date !== r.due_date;
            const exp = expenses[r.expense_id];
            const catId = r.category_id_override || exp?.category_id;
            const venId = r.vendor_id_override || exp?.vendor_id;
            const planned = Number(r.effective_amount || 0);
            const paid = Number(r.total_paid || 0);
            const remaining = planned - paid;
            const overdue =
              (r.status === "planned" || r.status === "partially_paid") &&
              r.due_date < new Date().toISOString().slice(0, 10);
            // Iter40: badge "Rata X/N" per le scadenze di un piano rateale.
            // Per le ricorrenze e le ordinarie non lo mostriamo — uniche
            // candidate sono le occurrences di un expense con
            // recurrence_type === 'custom_plan'.
            const isPlanInstallment =
              exp?.recurrence_type === "custom_plan" &&
              !!r.sequence_no &&
              r.status === "planned";
            const planTotal = exp?.installment_total || 0;
            return (
              <li
                key={r.id}
                data-testid={`occurrence-row-${r.id}`}
                className={
                  "relative grid items-center gap-4 py-1.5 pl-4 pr-4 transition-colors hover:bg-stone-50/70 " +
                  "grid-cols-1 lg:grid-cols-[100px_minmax(0,1fr)_140px_220px] " +
                  (overdue
                    ? "bg-rose-50/80 hover:bg-rose-50 border-l-4 border-rose-500 pl-[calc(1rem-4px)]"
                    : "")
                }
              >
                {/* COL 1 — Date */}
                <div>
                  <div
                    className={
                      "font-mono text-xs font-bold leading-tight tabular-nums " +
                      (overdue ? "text-rose-800" : "text-stone-900")
                    }
                  >
                    {fmtDate(r.due_date)}
                  </div>
                  {postponed && (
                    <div className="text-[10px] leading-tight text-blue-700">
                      ↻ da {fmtDate(r.original_due_date)}
                    </div>
                  )}
                </div>

                {/* COL 2 — Description block */}
                <div className="min-w-0">
                  <div className="truncate text-xs font-semibold leading-tight text-stone-900">
                    {r.effective_title || exp?.title || "—"}
                  </div>
                  <div className="truncate text-[10px] leading-tight text-stone-500">
                    {[
                      venMap[venId]?.name || "Senza fornitore",
                      catMap[catId]?.name || "Senza categoria",
                      recurrenceIT[exp?.recurrence_type] || "—",
                    ].join(" • ")}
                  </div>
                  {r.notes && (
                    <div className="truncate text-[10px] italic leading-tight text-stone-400">
                      {r.notes}
                    </div>
                  )}
                  {/* Tags + Document badge row */}
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <TagList
                      tags={tagsByEntity[r.id] || []}
                      size="sm"
                      max={4}
                      onRemove={async (t) => {
                        try {
                          await (await import("@/lib/api")).detachTag(
                            t.id,
                            "occurrence",
                            r.id,
                          );
                          refetchTags();
                        } catch {
                          /* silent */
                        }
                      }}
                    />
                    <TagAssignmentPopover
                      entityType="occurrence"
                      entityId={r.id}
                      currentTagIds={(tagsByEntity[r.id] || []).map((t) => t.id)}
                      onChange={() => refetchTags()}
                      testIdPrefix={`occurrence-tag-${r.id}`}
                    />
                    {(r.doc_type_id || r.doc_number) && (
                      <DocumentBadge
                        type={documentTypesById[r.doc_type_id]}
                        number={r.doc_number}
                        date={r.doc_date}
                        testId={`doc-badge-occ-${r.id}`}
                      />
                    )}
                  </div>
                </div>

                {/* COL 3 — Amount (visually dominant, no overlay badges) */}
                <div className="text-left lg:text-right">
                  <div
                    className={
                      "font-mono text-lg font-bold leading-tight tabular-nums " +
                      (overdue ? "text-rose-900" : "text-stone-900")
                    }
                  >
                    {EUR(planned)}
                  </div>
                  {paid > 0 && r.status !== "paid" && (
                    <div className="font-mono text-[10px] leading-tight tabular-nums text-emerald-700">
                      Pagato {EUR(paid)}
                      {remaining > 0 && (
                        <span
                          className={
                            "ml-2 " +
                            (overdue ? "text-rose-700" : "text-stone-500")
                          }
                        >
                          · Residuo {EUR(remaining)}
                        </span>
                      )}
                    </div>
                  )}
                  {paid === 0 &&
                    r.status !== "paid" &&
                    r.status !== "cancelled" &&
                    r.status !== "skipped" &&
                    remaining > 0 && (
                      <div
                        className={
                          "font-mono text-[10px] leading-tight tabular-nums " +
                          (overdue ? "text-rose-700" : "text-stone-500")
                        }
                      >
                        Residuo {EUR(remaining)}
                      </div>
                    )}
                  {r.status === "paid" && paid === 0 && (
                    <div className="font-mono text-[10px] leading-tight tabular-nums text-emerald-700">
                      ✓ Saldata
                    </div>
                  )}
                </div>

                {/* COL 4 — Status + Actions (separati: stato sopra, azioni accanto) */}
                <div className="flex items-center justify-start gap-3 lg:justify-end">
                  <div className="flex min-w-0 flex-col items-start gap-1 lg:items-end">
                    {overdue ? (
                      <span
                        data-testid={`badge-overdue-${r.id}`}
                        className="inline-flex items-center whitespace-nowrap rounded-full bg-rose-600 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white shadow-sm"
                      >
                        Scaduta
                      </span>
                    ) : isPlanInstallment ? (
                      <InstallmentBadge
                        sequence={r.sequence_no}
                        total={planTotal}
                      />
                    ) : (
                      <StatusBadge
                        status={r.status}
                        postponed={postponed}
                      />
                    )}
                    {r.status === "paid" && r.last_payment_at && (
                      <span
                        data-testid={`paid-date-${r.id}`}
                        className="whitespace-nowrap font-mono text-[10px] leading-tight tabular-nums text-stone-500"
                      >
                        Pagata il {fmtDate(r.last_payment_at)}
                      </span>
                    )}
                    {overdue && r.status === "partially_paid" && (
                      <span className="inline-flex items-center whitespace-nowrap rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800">
                        Parziale
                      </span>
                    )}
                    {overdue && postponed && (
                      <span className="inline-flex items-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium uppercase text-blue-700">
                        Rinviata
                      </span>
                    )}
                  </div>
                  <RowActions
                    r={r}
                    overdue={overdue}
                    onPay={() => openPay(r)}
                    onEdit={() => openEdit(r)}
                    onPostpone={() => openPostpone(r)}
                    onSkip={() => doSkip(r)}
                    onMarkNotDue={() => doMarkNotDue(r)}
                    onRestore={() => doRestore(r)}
                    onHistory={() => openHistory(r)}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {view === "calendar" && (
        <OccurrencesCalendar
          rows={rows}
          expenses={expenses}
          vendors={venMap}
          categories={catMap}
          initialMonth={filters.date_from}
          onChanged={load}
        />
      )}

      {/* Edit single occurrence */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="dialog-edit-occurrence">
          <DialogHeader>
            <DialogTitle>Modifica singola scadenza</DialogTitle>
            <DialogDescription>
              Questa modifica riguarda <strong>solo questa scadenza</strong>.
              Le altre scadenze della serie non vengono toccate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo pianificato (override)</Label>
              <StableInput
                type="number"
                step="0.01"
                data-testid="input-edit-amount"
                value={editAmount}
                onCommit={setEditAmount}
              />
            </div>
            <div>
              <Label>Note</Label>
              <StableTextarea
                data-testid="input-edit-notes"
                value={editNotes}
                onCommit={setEditNotes}
              />
            </div>
            <DocumentReferenceFields
              value={editDoc}
              onChange={setEditDoc}
              entityType="occurrence"
              entityId={editRow?.id}
              defaultOpen={!!(editDoc.doc_type_id || editDoc.doc_number)}
              testIdPrefix="edit-occurrence-docref"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button data-testid="btn-confirm-edit" onClick={submitEdit}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="dialog-pay">
          <DialogHeader>
            <DialogTitle>Registra pagamento</DialogTitle>
            <DialogDescription>
              Anche pagamenti parziali sono supportati. Lo stato viene ricalcolato automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Importo</Label>
              <StableInput
                type="number"
                step="0.01"
                data-testid="input-pay-amount"
                value={payAmount}
                onCommit={setPayAmount}
              />
              {payRow && (
                <p className="mt-1 text-xs text-stone-500">
                  Pianificato {EUR(payRow.effective_amount)} — già pagato {EUR(payRow.total_paid)}
                </p>
              )}
            </div>
            <div>
              <Label>Data pagamento (cassa)</Label>
              <Input
                type="date"
                data-testid="input-pay-date"
                value={payDate}
                onChange={(e) => setPayDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Metodo di pagamento</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger data-testid="select-pay-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentMethodOptions.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`select-pay-method-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Riferimento (es. CRO)</Label>
              <StableInput
                data-testid="input-pay-ref"
                value={payRef}
                onCommit={setPayRef}
              />
            </div>
            <DocumentReferenceFields
              value={payDoc}
              onChange={setPayDoc}
              defaultOpen={!!(payDoc.doc_type_id || payDoc.doc_number)}
              testIdPrefix="pay-docref"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Annulla</Button>
            <Button data-testid="btn-confirm-pay" onClick={submitPay}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Postpone */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent data-testid="dialog-postpone">
          <DialogHeader>
            <DialogTitle>Rinvia scadenza</DialogTitle>
            <DialogDescription>
              La cadenza della serie <strong>non si sposta</strong>: viene rinviata solo questa scadenza.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nuova data scadenza</Label>
              <Input
                type="date"
                data-testid="input-postpone-date"
                value={postDate}
                onChange={(e) => setPostDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostOpen(false)}>Annulla</Button>
            <Button data-testid="btn-confirm-postpone" onClick={submitPostpone}>Rinvia</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment history */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent data-testid="dialog-history" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <div className="flex items-center justify-between gap-3">
                <span>Storico pagamenti</span>
                {historyPayments.length > 0 && historyRow && (
                  <ExportButtons
                    rows={historyPayments.map((p) => ({
                      paid_at: p.paid_at,
                      method: paymentMethodIT[p.method] || paymentMethodIT.unspecified,
                      reference: p.reference || "",
                      paid_amount: p.paid_amount,
                      occurrence_due_date: historyRow.due_date,
                    }))}
                    columns={[
                      { key: "paid_at", header: "Data pagamento", type: "date" },
                      { key: "method", header: "Metodo" },
                      { key: "reference", header: "Riferimento" },
                      {
                        key: "paid_amount",
                        header: "Importo (€)",
                        type: "currency",
                      },
                      {
                        key: "occurrence_due_date",
                        header: "Scadenza riferita",
                        type: "date",
                      },
                    ]}
                    filenameBase={buildFilename(
                      `storico_pagamenti`,
                      { date_from: historyRow.due_date },
                    )}
                    sheetName="Pagamenti"
                    testId="export-payments-history"
                  />
                )}
              </div>
            </DialogTitle>
            <DialogDescription>
              {historyRow && (
                <>
                  Scadenza {fmtDate(historyRow.due_date)} — pianificato{" "}
                  <strong>{EUR(historyRow.effective_amount)}</strong>, pagato{" "}
                  <strong>{EUR(historyRow.total_paid)}</strong>, residuo{" "}
                  <strong>
                    {EUR(
                      Number(historyRow.effective_amount || 0) -
                        Number(historyRow.total_paid || 0),
                    )}
                  </strong>
                  .
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Metodo</TableHead>
                <TableHead>Riferimento</TableHead>
                <TableHead className="text-right">Importo</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyPayments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="py-6 text-center text-sm text-stone-500">
                    Nessun pagamento registrato.
                  </TableCell>
                </TableRow>
              )}
              {historyPayments.map((p) => (
                <TableRow key={p.id} data-testid={`history-payment-${p.id}`}>
                  <TableCell>{fmtDate(p.paid_at)}</TableCell>
                  <TableCell>
                    <span
                      data-testid={`history-method-${p.id}`}
                      className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-medium text-stone-700"
                    >
                      {paymentMethodIT[p.method] || paymentMethodIT.unspecified}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-stone-600">{p.reference || "—"}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {EUR(p.paid_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-edit-payment-${p.id}`}
                      onClick={() => openEditPayment(p)}
                      className="mr-1"
                    >
                      Modifica
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`btn-delete-payment-${p.id}`}
                      onClick={() => deletePaymentAndReload(p.id)}
                    >
                      Elimina
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit payment dialog (fix wrong date / amount / method / reference) */}
      <Dialog
        open={!!editPayment}
        onOpenChange={(o) => !o && setEditPayment(null)}
      >
        <DialogContent data-testid="dialog-edit-payment">
          <DialogHeader>
            <DialogTitle>Modifica pagamento</DialogTitle>
            <DialogDescription>
              Correggi data, importo, metodo o riferimento del pagamento già
              registrato. Lo stato della scadenza viene ricalcolato in
              automatico.
            </DialogDescription>
          </DialogHeader>
          {editPayment && (
            <div className="space-y-3">
              <div>
                <Label>Importo</Label>
                <Input
                  data-testid="input-edit-payment-amount"
                  type="number"
                  step="0.01"
                  value={epAmount}
                  onChange={(e) => setEpAmount(e.target.value)}
                />
              </div>
              <div>
                <Label>Data pagamento (cassa)</Label>
                <Input
                  data-testid="input-edit-payment-date"
                  type="date"
                  value={epDate}
                  onChange={(e) => setEpDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Metodo di pagamento</Label>
                <Select value={epMethod} onValueChange={setEpMethod}>
                  <SelectTrigger data-testid="select-edit-payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentMethodOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Riferimento (es. CRO)</Label>
                <Input
                  data-testid="input-edit-payment-ref"
                  value={epRef}
                  onChange={(e) => setEpRef(e.target.value)}
                />
              </div>
              <DocumentReferenceFields
                value={epDoc}
                onChange={setEpDoc}
                entityType="payment"
                entityId={editPayment?.id}
                defaultOpen={!!(epDoc.doc_type_id || epDoc.doc_number)}
                testIdPrefix="edit-payment-docref"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPayment(null)}>
              Annulla
            </Button>
            <Button
              data-testid="btn-submit-edit-payment"
              onClick={submitEditPayment}
            >
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NuovaScadenzaChoiceDialog
        open={newChoiceOpen}
        onOpenChange={setNewChoiceOpen}
      />

      <AlertDialog
        open={!!notDueTarget}
        onOpenChange={(o) => !o && setNotDueTarget(null)}
      >
        <AlertDialogContent data-testid="dialog-not-due">
          <AlertDialogHeader>
            <AlertDialogTitle>Segnare la scadenza come &quot;Non dovuta&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              Usa questa azione quando l&apos;importo <strong>non sarà mai versato</strong>
              {" "}(es. F24 compensato, IVA a credito, tributo annullato).
              La scadenza sparirà dagli elenchi operativi ma resta consultabile
              nello storico (toggle <em>Mostra chiuse</em>).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-not-due"
              onClick={confirmMarkNotDue}
            >
              Segna come non dovuta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!deletePayTarget}
        onOpenChange={(o) => !o && setDeletePayTarget(null)}
      >
        <AlertDialogContent data-testid="dialog-delete-payment">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;importo verrà rimosso dal totale pagato della scadenza
              e da Prima Nota. L&apos;operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-delete-payment"
              onClick={confirmDeletePayment}
            >
              Elimina pagamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Compact actions for a Scadenze row.
 * Strong action hierarchy:
 *  - Paga = primary inline button (visually dominant)
 *  - Rinvia / Modifica / Salta / Storico / Apri serie = secondary, inside ⋯ menu
 */
function RowActions({
  r, overdue, onPay, onEdit, onPostpone, onSkip, onHistory,
  onMarkNotDue, onRestore,
}) {
  const finished =
    r.status === "paid" ||
    r.status === "cancelled" ||
    r.status === "not_due" ||
    r.status === "skipped";
  const canMarkNotDue = r.status === "planned";
  const canRestore =
    r.status === "cancelled" || r.status === "not_due" || r.status === "skipped";
  return (
    <div className="flex items-center justify-end gap-1.5">
      {!finished && (
        <Button
          size="sm"
          data-testid={`btn-pay-${r.id}`}
          onClick={onPay}
          className={
            "h-8 px-3 font-semibold shadow-sm " +
            (overdue
              ? "bg-rose-600 text-white hover:bg-rose-700"
              : "")
          }
        >
          Paga
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="h-8 w-8 p-0"
            data-testid={`btn-actions-${r.id}`}
            aria-label="Altre azioni"
          >
            <MoreHorizontal size={16} strokeWidth={1.8} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {!finished && (
            <>
              <DropdownMenuItem
                data-testid={`btn-postpone-${r.id}`}
                onClick={onPostpone}
              >
                Rinvia
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`btn-edit-${r.id}`}
                onClick={onEdit}
              >
                Modifica
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`btn-skip-${r.id}`}
                onClick={onSkip}
              >
                Salta
              </DropdownMenuItem>
              {canMarkNotDue && (
                <DropdownMenuItem
                  data-testid={`btn-not-due-${r.id}`}
                  onClick={onMarkNotDue}
                >
                  Segna come non dovuta
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}
          {canRestore && (
            <>
              <DropdownMenuItem
                data-testid={`btn-restore-${r.id}`}
                onClick={onRestore}
              >
                Ripristina come pianificata
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            data-testid={`btn-history-${r.id}`}
            onClick={onHistory}
          >
            Storico pagamenti
          </DropdownMenuItem>
          {Number(r.total_paid || 0) > 0 && (
            <DropdownMenuItem asChild>
              <Link
                to={`/prima-nota?occurrence_id=${r.id}`}
                data-testid={`btn-prima-nota-${r.id}`}
              >
                Vai in Prima Nota
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link
              to={`/expenses/new?from_expense=${r.expense_id}`}
              data-testid={`btn-duplicate-${r.id}`}
            >
              Duplica
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to={`/expenses/${r.expense_id}/edit`}
              data-testid={`btn-open-expense-${r.id}`}
            >
              Apri serie
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Compact KPI strip — totals per category for the currently filtered rows.
 * Shows planned amount per category, sorted descending, with an "Altre" pill
 * if there are too many categories to fit on one line.
 */
function CategoryKpiStrip({
  rows,
  expenses,
  catMap,
  filters,
  onPick,
  activeCategoryId,
  open = true,
  onToggle,
}) {
  const groups = useMemo(() => {
    const acc = new Map();
    for (const r of rows || []) {
      const exp = expenses[r.expense_id];
      const catId = r.category_id_override || exp?.category_id || "__none__";
      const name = catMap[catId]?.name || "Senza categoria";
      const cur = acc.get(catId) || {
        id: catId,
        name,
        planned: 0,
        paid: 0,
        count: 0,
      };
      cur.planned += Number(r.effective_amount || 0);
      cur.paid += Number(r.total_paid || 0);
      cur.count += 1;
      acc.set(catId, cur);
    }
    return Array.from(acc.values()).sort((a, b) => b.planned - a.planned);
  }, [rows, expenses, catMap]);

  if (!rows || rows.length === 0) return null;

  const total = groups.reduce((s, g) => s + g.planned, 0);

  // Period label uses the currently selected date range (Italian dd/MM/yyyy)
  const periodLabel = (() => {
    if (!filters?.date_from || !filters?.date_to) return null;
    return `${fmtDate(filters.date_from)} – ${fmtDate(filters.date_to)}`;
  })();

  const isFilterable = (id) => id && id !== "__none__";

  return (
    <div
      data-testid="category-kpi-strip"
      className="mb-3 rounded-xl border border-stone-200 bg-white px-3 py-2"
    >
      <button
        type="button"
        data-testid="category-kpi-toggle"
        onClick={onToggle}
        className="flex w-full flex-wrap items-baseline justify-between gap-2 text-left"
      >
        <div className="flex flex-wrap items-baseline gap-2">
          {onToggle && (
            open ? (
              <ChevronDown size={12} className="text-stone-500" />
            ) : (
              <ChevronRight size={12} className="text-stone-500" />
            )
          )}
          <span className="text-[10px] font-medium uppercase tracking-wider text-stone-500">
            Totali per categoria
          </span>
          {periodLabel && (
            <span
              data-testid="category-kpi-period"
              className="text-[11px] text-stone-500"
            >
              — periodo: <strong>{periodLabel}</strong>
            </span>
          )}
        </div>
        <span className="font-mono text-xs tabular-nums text-stone-500">
          Totale {EUR(total)} · {rows.length} scadenz
          {rows.length === 1 ? "a" : "e"}
        </span>
      </button>
      {open && (
        <>
          {activeCategoryId && (
            <div
              data-testid="category-kpi-active-banner"
              className="mb-2 mt-2 flex items-center justify-between gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-1 text-xs text-blue-900"
            >
              <span>
                Filtro attivo:{" "}
                <strong>{catMap[activeCategoryId]?.name || "Categoria"}</strong>
              </span>
              <button
                type="button"
                data-testid="kpi-clear-category"
                onClick={() => onPick && onPick("")}
                className="rounded-md border border-blue-200 bg-white px-2 py-0.5 text-[11px] font-medium text-blue-800 hover:bg-blue-100"
              >
                Rimuovi filtro
              </button>
            </div>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {groups.map((g) => {
          const isActive = activeCategoryId === g.id;
          const clickable = isFilterable(g.id) && typeof onPick === "function";
          const Wrapper = clickable ? "button" : "div";
          return (
            <Wrapper
              key={g.id}
              type={clickable ? "button" : undefined}
              data-testid={`kpi-cat-${g.id}`}
              onClick={
                clickable ? () => onPick(isActive ? "" : g.id) : undefined
              }
              title={
                clickable
                  ? isActive
                    ? "Rimuovi filtro categoria"
                    : `Filtra per ${g.name}`
                  : undefined
              }
              className={
                "inline-flex items-baseline gap-1.5 rounded-full border px-3 py-1 transition-colors " +
                (isActive
                  ? "border-blue-600 bg-blue-600 text-white"
                  : "border-stone-200 bg-stone-50 text-stone-700") +
                (clickable
                  ? " cursor-pointer hover:border-stone-300 hover:bg-stone-100"
                  : " cursor-default") +
                (isActive
                  ? " hover:bg-blue-700 hover:border-blue-700"
                  : "")
              }
            >
              <span
                className={
                  "text-[11px] font-medium " +
                  (isActive ? "text-white" : "text-stone-700")
                }
              >
                {g.name}
              </span>
              <span
                className={
                  "font-mono text-[11px] font-semibold tabular-nums " +
                  (isActive ? "text-white" : "text-stone-900")
                }
              >
                {EUR(g.planned)}
              </span>
              <span
                className={
                  "font-mono text-[10px] tabular-nums " +
                  (isActive ? "text-blue-100" : "text-stone-400")
                }
              >
                ({g.count})
              </span>
            </Wrapper>
          );
        })}
          </div>
        </>
      )}
    </div>
  );
}


/**
 * "Da pagare nel periodo filtrato" pill, shown above the table.
 *
 * Reflects the FULL exposure of the active filter window (including paid
 * items hidden by the "Mostra pagate" toggle) so the residual figure is
 * stable regardless of UI cosmetics. Excludes skipped/cancelled items.
 */
function PeriodExposurePill({ rawRows, loading, filters, testId }) {
  const totals = useMemo(() => {
    let planned = 0;
    let paid = 0;
    let count = 0;
    let openCount = 0;
    for (const r of rawRows || []) {
      if (r.status === "skipped" || r.status === "cancelled") continue;
      planned += Number(r.effective_amount || 0);
      paid += Number(r.total_paid || 0);
      count += 1;
      if (r.status === "planned" || r.status === "partially_paid") openCount += 1;
    }
    return { planned, paid, remaining: planned - paid, count, openCount };
  }, [rawRows]);

  const tone = totals.remaining > 0 ? "amber" : "emerald";
  const colors = {
    amber: {
      wrap: "border-amber-200 bg-amber-50/60",
      title: "text-amber-800",
      value: "text-amber-900",
      hint: "text-amber-700",
    },
    emerald: {
      wrap: "border-emerald-200 bg-emerald-50/60",
      title: "text-emerald-800",
      value: "text-emerald-900",
      hint: "text-emerald-700",
    },
  }[tone];

  const periodLabel = filters?.date_from && filters?.date_to
    ? `${fmtDate(filters.date_from)} → ${fmtDate(filters.date_to)}`
    : null;

  return (
    <div
      data-testid={testId}
      className={
        "mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border px-3 py-1.5 " +
        colors.wrap
      }
    >
      <span
        className={
          "text-[10px] font-semibold uppercase tracking-wider " + colors.title
        }
      >
        Da pagare nel periodo
      </span>
      {periodLabel && (
        <span
          data-testid={`${testId}-period`}
          className="font-mono text-[11px] tabular-nums text-stone-600"
        >
          {periodLabel}
        </span>
      )}
      <span
        data-testid={`${testId}-remaining`}
        className={"font-mono text-base font-semibold tabular-nums " + colors.value}
      >
        {EUR(totals.remaining)}
      </span>
      <span className={"text-[11px] " + colors.hint}>
        Pianificato <span className="font-mono tabular-nums">{EUR(totals.planned)}</span>
        {" · "}Pagato <span className="font-mono tabular-nums">{EUR(totals.paid)}</span>
        {" · "}{totals.count} {totals.count === 1 ? "voce" : "voci"}
        {totals.openCount > 0
          ? ` · ${totals.openCount} ${totals.openCount === 1 ? "aperta" : "aperte"}`
          : ""}
      </span>
      {loading && (
        <span className="ml-auto text-[10px] text-stone-400">Aggiornamento…</span>
      )}
    </div>
  );
}
